import { RelatedEntityType } from "@dune/types";
import {
	Badge,
	EmailTemplateEditor,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { AlertTriangle, Lock } from "lucide-react";
import React from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { getEntityLabel } from "./constants";
import { EmailTemplateFormData } from "./types";

interface EmailTemplateFormProps {
	control: Control<EmailTemplateFormData>;
	errors: FieldErrors<EmailTemplateFormData>;
	isEditing: boolean;
	onImageUpload: (file: File) => Promise<string>;
	invalidPlaceholders?: string[];
	template?: { entityModel: RelatedEntityType } | null;
	copyTemplate?: { entityModel: RelatedEntityType } | null;
	isCopying?: boolean;
}

export const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({
	control,
	errors,
	isEditing,
	onImageUpload,
	invalidPlaceholders = [],
	template,
	copyTemplate,
	isCopying = false,
}) => {
	return (
		<div className="space-y-6">
			{invalidPlaceholders.length > 0 && (
				<div className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 rounded-md">
					<AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
					<div className="flex-1">
						<div className="font-medium text-red-900 text-sm">
							Placeholders invalides détectés:
						</div>
						<div className="mt-2 space-y-1">
							{invalidPlaceholders.map((placeholder) => (
								<div
									key={placeholder}
									className="font-mono text-xs bg-red-100 text-red-800 px-2 py-1 rounded"
								>
									{`{{${placeholder}}}`}
								</div>
							))}
						</div>
						<p className="mt-2 text-xs text-red-700">
							Ces placeholders ne correspondent à aucun champ disponible pour
							l'entité sélectionnée.
						</p>
					</div>
				</div>
			)}

			<div className="grid grid-cols-2 gap-4">
				<Controller
					name="name"
					control={control}
					rules={{ required: "Le nom est requis" }}
					render={({ field }) => (
						<Input
							label="Nom du template"
							{...field}
							error={errors.name?.message}
						/>
					)}
				/>

				<Controller
					name="subject"
					control={control}
					rules={{ required: "Le sujet est requis" }}
					render={({ field }) => (
						<Input
							label="Sujet de l'email"
							placeholder="Ex: {{society.denomination}} - Nouvelle proposition"
							{...field}
							error={errors.subject?.message}
						/>
					)}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4">
				<Controller
					name="entityModel"
					control={control}
					rules={{ required: "L'entité est requise" }}
					render={({ field }) => {
						const actualValue =
							(isEditing && template?.entityModel) ||
							field.value ||
							RelatedEntityType.BROKER;

						const selectKey = `entity-select-${isEditing ? template?.entityModel || "new" : isCopying ? copyTemplate?.entityModel || "copy" : "default"}`;

						return (
							<div>
								<label
									htmlFor="entityModel"
									className="text-sm font-medium text-foreground mb-2 block"
								>
									Entité concernée
									{isEditing && (
										<Badge
											variant="secondary"
											className="ml-2 inline-flex items-center gap-1"
										>
											<Lock className="h-3 w-3" />
											Verrouillé
										</Badge>
									)}
								</label>
								<Select
									key={selectKey}
									value={actualValue}
									onValueChange={field.onChange}
									disabled={isEditing}
								>
									<SelectTrigger id="entityModel">
										<SelectValue placeholder="Sélectionner une entité" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={RelatedEntityType.BROKER}>
											{getEntityLabel(RelatedEntityType.BROKER)}
										</SelectItem>
										<SelectItem value={RelatedEntityType.CONSTRUCTION_SITE}>
											{getEntityLabel(RelatedEntityType.CONSTRUCTION_SITE)}
										</SelectItem>
										<SelectItem value={RelatedEntityType.PROPOSITION}>
											{getEntityLabel(RelatedEntityType.PROPOSITION)}
										</SelectItem>
									</SelectContent>
								</Select>
								{errors.entityModel && (
									<p className="text-destructive text-sm mt-1">
										{errors.entityModel.message}
									</p>
								)}
							</div>
						);
					}}
				/>
			</div>

			<Controller
				name="htmlContent"
				control={control}
				rules={{ required: "Le contenu est requis" }}
				render={({ field }) => (
					<div>
						<EmailTemplateEditor
							content={field.value}
							onUpdate={field.onChange}
							onImageUpload={onImageUpload}
							label="Contenu du template"
							placeholder="Rédigez votre template d'email avec des placeholders comme {{society.denomination}}..."
						/>
						{errors.htmlContent && (
							<p className="text-destructive text-sm mt-1">
								{errors.htmlContent.message}
							</p>
						)}
					</div>
				)}
			/>
		</div>
	);
};
